import {
Component,
OnInit,
OnDestroy,
OnChanges,
Input,
SimpleChanges,
Output,
EventEmitter,
Inject
} from '@angular/core';

import {
FormGroup,
Validators
} from '@angular/forms';

import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DatexFormControl } from './models/datex-form-control';
import {
TextBoxModel,
NumberBoxModel,
SelectBoxModel,
ESelectBoxType,
DateBoxModel,
CheckBoxModel,
TextModel,
LabelModel,
ButtonModel,
SplitButtonModel,
SeparatorModel,
ImageModel,
DrawModel,
CodeBoxModel,
ButtonStyles
} from './models/control';
import { Styles, ControlContainerStyles } from './models/style';
import { FieldModel } from './models/field';
import { FieldsetModel } from './models/fieldset';
import { ToolModel } from './models/tool';
import * as vkbeautify from 'vkbeautify';
import { BaseComponent } from './components/base.component';
import { CodemirrorComponent } from '@ctrl/ngx-codemirror';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { FootprintManager_ShellService } from './FootprintManager.shell.service';
import { FootprintManager_OperationService } from './FootprintManager.operation.service';
import { FootprintManager_DatasourceService } from './FootprintManager.datasource.index';
import { FootprintManager_FlowService } from './FootprintManager.flow.index';
import { FootprintManager_ReportService } from './FootprintManager.report.index';
import { FootprintManager_LocalizationService } from './FootprintManager.localization.service';
import { FootprintManager_view_xml_payload_ComponentContextService } from './FootprintManager.view_xml_payload.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './FootprintManager.frontend.types'
import { $frontendTypes as $types} from './FootprintManager.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'FootprintManager-view_xml_payload',
  templateUrl: './FootprintManager.view_xml_payload.component.html'
})
export class FootprintManager_view_xml_payloadComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges
{

inParams: { payload?: any, title?: string } = { payload: null, title: null };
//#region Inputs
@Input('payload') set $inParams_payload(v: any) {
  this.inParams.payload = v;
}
get $inParams_payload(): any {
  return this.inParams.payload;
}
@Input('title') set $inParams_title(v: string) {
  this.inParams.title = v;
}
get $inParams_title(): string {
  return this.inParams.title;
}
//#endregion Inputs

//#region Outputs
@Output()
$finish = new EventEmitter();
//#endregion

//#region title
// Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
// The title is often meant to be shown from the parent (shell breadcrumb for example)
// and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
// the parent has already been checked and the child now change something on the parent 
// in dev, CD is run twice
$titleChange = new EventEmitter<string>(true);
private $_title: string;
get title(): string {
  return this.$_title;
}
set title(t: string) {
  this.$_title = t;
  this.$titleChange.emit(this.$_title);
}
//#endregion title
codeMirrorOptions = {
theme: 'base16-light',
mode: 'application/xml',
lineNumbers: true,
lineWrapping: true,
foldGutter: true,
gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter', 'CodeMirror-lint-markers'],
autoCloseBrackets: true,
matchBrackets: true,
lint: true
};

mode = 'application/xml';

value: string;

//#region Variables
//#endregion
//#region Events

//#endregion

toolbar = {
  beautify: new ToolModel(new ButtonModel('beautify', new ButtonStyles(null, null), false, false, false, 'Beautify', 'icon-ic_fluent_balloon_20_regular', null)
, false)
};

constructor(
private $utils: UtilsService,
private $settings: SettingsValuesService,
private $shell: FootprintManager_ShellService,
private $datasources: FootprintManager_DatasourceService,
private $flows: FootprintManager_FlowService,
private $reports: FootprintManager_ReportService,
private $localization: FootprintManager_LocalizationService,
private $operations: FootprintManager_OperationService,
private $logger: CleanupLoggerService,
private $context: FootprintManager_view_xml_payload_ComponentContextService,
) {
  super();
}

ngOnInit(): void {
  this.$init();
}

private $isFirstNgOnChanges = true;
ngOnChanges(changes: SimpleChanges): void {
  if (this.$isFirstNgOnChanges) {
    this.$isFirstNgOnChanges = false;
  } else {
    this.$init();
  }
}

private $unsubscribe$ = new Subject();
ngOnDestroy(): void {
  this.$unsubscribe$.next(null);
  this.$unsubscribe$.complete();
}

initialized = false;

async $init() {
this.title = 'view_xml_payload';

const $codeEditor = this;
const $utils = this.$utils;

this.value = $codeEditor.inParams.payload;

await this.on_init();

this.initialized = true;
}

onCodeMirrorLoaded(editor: CodemirrorComponent){
  //temporary fix to reset the left margin on the gutters. on load an extra left space is being added to the CodeMirror-gutters div element, which causes it to overlap the code content. this refresh re-calculates the space needed and removes the gap. a better implementation could be possible.
  setTimeout(() =>{editor.codeMirror.refresh() }, 300);
}

close() {
this.$finish.emit();
}

beautify(): void {
this.value = this.mode === 'application/xml' ? vkbeautify.xml(this.value) : vkbeautify.json(this.value);
}

minify(): void {
this.value = this.mode === 'application/xml' ? vkbeautify.xmlmin(this.value, [, true]) : vkbeautify.jsonmin(this.value);
}

refresh(
skipParent = false,
skipChildren = false,
childToSkip: string = null) {
}

//#region private flows
on_beautify_clicked(event = null) {
  return this.on_beautify_clickedInternal(
    this,
this.$shell,
    this.$datasources,
    this.$flows,
    this.$reports,
    this.$settings,
    this.$operations,
    this.$utils,
    this.$context,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    // this.localization,
    event);
}
async on_beautify_clickedInternal(
  $codeEditor: FootprintManager_view_xml_payloadComponent,

  $shell: FootprintManager_ShellService,
  $datasources: FootprintManager_DatasourceService,
  $flows: FootprintManager_FlowService,
  $reports: FootprintManager_ReportService,
  $settings: SettingsValuesService,
  $operations: FootprintManager_OperationService,
  $utils: UtilsService,
  $context: FootprintManager_view_xml_payload_ComponentContextService,
  // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
  //$l10n: FootprintManager_LocalizationService,
  $event: any
) {
//O.Arias - 02/06/2024

$codeEditor.beautify();
}
on_init(event = null) {
  return this.on_initInternal(
    this,
this.$shell,
    this.$datasources,
    this.$flows,
    this.$reports,
    this.$settings,
    this.$operations,
    this.$utils,
    this.$context,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    // this.localization,
    event);
}
async on_initInternal(
  $codeEditor: FootprintManager_view_xml_payloadComponent,

  $shell: FootprintManager_ShellService,
  $datasources: FootprintManager_DatasourceService,
  $flows: FootprintManager_FlowService,
  $reports: FootprintManager_ReportService,
  $settings: SettingsValuesService,
  $operations: FootprintManager_OperationService,
  $utils: UtilsService,
  $context: FootprintManager_view_xml_payload_ComponentContextService,
  // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
  //$l10n: FootprintManager_LocalizationService,
  $event: any
) {
//O.Arias - 02/06/2024

$codeEditor.beautify();

let title: string = $codeEditor.inParams.title ?? "";

if (title === "") {
    $codeEditor.title = "Code Editor";
} else {
    $codeEditor.title = title;
};
}
//#endregion private flows
}